import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Repeat } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { DateInput } from '@/components/ui/date-input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAccounts } from '@/hooks/useAccounts'
import { useCategories, buildPicker } from '@/hooks/useCategories'
import {
  useSaveTransaction,
  useSaveInstallments,
  useEditRecurring,
  type TransactionInput,
  type EditScope,
} from '@/hooks/useTransactions'
import { enabledCurrencyList, hasHistorical, formatCurrency } from '@/lib/currency'
import { suggestStatementMonth } from '@/lib/cards'
import { splitCents } from '@/lib/finance'
import { useCurrentRates, convertBetween } from '@/hooks/useCurrentRates'
import { useHousehold } from '@/context/HouseholdContext'
import { Badge } from '@/components/ui/badge'
import { useTags } from '@/hooks/useTags'
import { cn } from '@/lib/utils'
import { accountComboOptions } from '@/lib/accountOptions'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'
import type { Transaction, TransactionKind } from '@/types/db'

interface FormValues {
  kind: 'income' | 'expense'
  account_id: string
  category_id: string | null
  amount: number
  currency: string
  date: string
  description?: string
  status: 'paid' | 'pending'
  // taxa manual opcional: mantida como string no form; convertida no submit.
  exchange_rate?: string
}

// Data inicial de um lançamento novo, dado o MÊS que a tela está exibindo.
// Mês corrente → hoje. Outro mês → dia 1 dele (você está em setembro, o
// lançamento nasce em setembro; antes vinha sempre com a data de hoje).
export function defaultDateForMonth(cursor: Date): string {
  const now = new Date()
  if (cursor.getFullYear() === now.getFullYear() && cursor.getMonth() === now.getMonth()) return todayISO()
  const d = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function todayISO() {
  // DATA LOCAL (não UTC): `toISOString()` à noite em UTC-3/UTC-6 já é o dia seguinte,
  // fazendo o lançamento novo nascer "amanhã" e `isBackdated` marcar hoje como passado.
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function TransactionFormDialog({
  open,
  onOpenChange,
  transaction,
  duplicateFrom,
  defaultAccountId,
  defaultDate,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  transaction?: Transaction | null
  /** Origem para DUPLICAR: pré-preenche os campos, mas a transação é NOVA
   *  (insere, não edita). Copia só os campos editáveis — não o id, recorrência
   *  ou nº de parcela. Ignorado se `transaction` estiver setado (edição vence). */
  duplicateFrom?: Transaction | null
  defaultAccountId?: string
  /** Data inicial ao CRIAR. A tela passa o mês que está sendo visto — se você
   *  está em setembro, o lançamento novo já nasce em setembro, não hoje. */
  defaultDate?: string
}) {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { baseCurrency, enabledCurrencies } = useHousehold()
  const { data: accounts } = useAccounts()
  const { data: categories } = useCategories()
  const save = useSaveTransaction()
  const saveInstallments = useSaveInstallments()
  const editRecurring = useEditRecurring()

  // Fonte dos VALORES INICIAIS: a transação em edição ou a que está sendo
  // duplicada. A decisão insere/edita continua olhando só `transaction` — ao
  // duplicar ele é null, então salva como nova.
  const isDuplicate = !transaction && !!duplicateFrom
  const source = transaction ?? duplicateFrom ?? null

  // Recorrência (só ao criar). 'none' = transação única.
  // Tags (coluna text[] com os NOMES). Sincroniza ao abrir/trocar de transação.
  const { data: allTags } = useTags()
  const [tags, setTags] = useState<string[]>([])
  useEffect(() => {
    if (open) setTags(source?.tags ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction, duplicateFrom])
  function toggleTag(name: string) {
    setTags((cur) => (cur.includes(name) ? cur.filter((t) => t !== name) : [...cur, name]))
  }

  // Escopo ao editar uma ocorrência recorrente.
  const [editScope, setEditScope] = useState<EditScope>('one')
  const isRecurringEdit = Boolean(transaction?.recurring_id)

  const schema = useMemo(
    () =>
      z.object({
        kind: z.enum(['income', 'expense']),
        account_id: z.string().uuid(t('dlg.tx.selectAccount')),
        category_id: z.string().uuid().nullable(),
        // Zero é PERMITIDO de propósito: é como se "mata" uma ocorrência recorrente que
        // não aconteceu (ex.: a limpeza não veio nesta semana). Lançar 0 casa a
        // ocorrência com a regra, então ela deixa de ser projetada no mês.
        amount: z.number({ message: t('dlg.tx.errAmount') }).nonnegative(t('dlg.tx.errNegative')),
        currency: z.string().min(3),
        date: z.string().min(1, t('dlg.tx.errDate')),
        description: z.string().optional(),
        status: z.enum(['paid', 'pending']),
        // taxa manual opcional: mantida como string no form; convertida no submit.
        exchange_rate: z.string().optional(),
      }),
    [t],
  )

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      kind: (source?.kind as 'income' | 'expense') ?? 'expense',
      account_id: source?.account_id ?? defaultAccountId ?? '',
      category_id: source?.category_id ?? null,
      amount: source?.amount ?? 0,
      currency: source?.currency ?? baseCurrency,
      date: source?.date ?? defaultDate ?? todayISO(),
      description: source?.description ?? '',
      status: source?.status ?? 'pending',
      exchange_rate: source?.exchange_rate != null ? String(source.exchange_rate) : '',
    },
  })

  const kind = watch('kind')
  const accountId = watch('account_id')
  const date = watch('date')
  const amountValue = watch('amount')
  const selectedAccount = accounts?.find((a) => a.id === accountId)
  const { data: rates } = useCurrentRates()

  // Moeda: por padrão segue a conta, mas o usuário pode trocar (ex.: compra em
  // USD num cartão BRL). Ao mudar a conta, volta a seguir a moeda da nova conta.
  const [currency, setCurrency] = useState<string>(source?.currency ?? baseCurrency)
  // Valor na moeda da conta (só quando a moeda difere). null = usar a sugestão.
  const [amountAccount, setAmountAccount] = useState<number | null>(source?.amount_account ?? null)
  const prevAccount = useRef<string>('')
  useEffect(() => {
    if (open) {
      const initAcc = source?.account_id ?? defaultAccountId ?? ''
      prevAccount.current = initAcc
      const acc = accounts?.find((a) => a.id === initAcc)
      setCurrency(source?.currency ?? acc?.currency ?? baseCurrency)
      setAmountAccount(source?.amount_account ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    if (accountId && accountId !== prevAccount.current) {
      prevAccount.current = accountId
      const acc = accounts?.find((a) => a.id === accountId)
      if (acc) setCurrency(acc.currency)
      setAmountAccount(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, accounts])

  // ------------------------- Mês da fatura (só cartão) -------------------------
  // A fatura leva o nome do mês em que VENCE (ver src/lib/cards.ts). O app sugere
  // qual é, a partir do fechamento + vencimento do cartão; o usuário sobrescreve.
  //
  // A fatura NUNCA pode ser anterior ao mês da compra (não se cobra algo antes de
  // acontecer) — o `min` do input garante isso, e useCardMonthFatura depende disso
  // para poder recortar por data no servidor.
  const isCard = selectedAccount?.type === 'cartao'
  const [stmtTouched, setStmtTouched] = useState(false)
  const [stmtMonth, setStmtMonth] = useState('') // 'YYYY-MM' (formato do <input type=month>)
  const suggestedStmt = date
    ? suggestStatementMonth(date, selectedAccount?.closing_day, selectedAccount?.due_day).slice(0, 7)
    : ''
  // Padrão do campo Fatura:
  //  - statement_month gravado → respeita.
  //  - EDITANDO sem statement_month → o mês da própria DATA, que é onde o
  //    lançamento já aparece (histórico tem statement_month nulo; a régua da
  //    fatura cai na data — ver faturaEndISO). Usar a sugestão aqui empurrava a
  //    fatura para o mês seguinte ao editar só a descrição, movendo o lançamento.
  //  - CRIANDO/duplicando → a sugestão (é compra nova; o app adianta a fatura).
  const defaultStmt =
    source?.statement_month?.slice(0, 7) ??
    (transaction ? (source?.date?.slice(0, 7) ?? '') : suggestedStmt)
  useEffect(() => {
    if (open) setStmtTouched(false)
  }, [open, transaction, duplicateFrom])
  useEffect(() => {
    if (!stmtTouched) setStmtMonth(defaultStmt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultStmt, stmtTouched])

  // ------------------------- Parcelamento (só cartão, ao criar) ----------------
  // Uma compra à vista ou dividida em N. Parcelado só faz sentido em cartão e só
  // ao CRIAR (editar uma parcela mexe numa transação já materializada).
  const canInstall = isCard && !transaction
  const [installMode, setInstallMode] = useState<'avista' | 'parcelado'>('avista')
  const [installCount, setInstallCount] = useState('2')
  const [installBasis, setInstallBasis] = useState<'parcela' | 'total'>('parcela')
  // Aba ativa do corpo do form (Geral / Cartão / Câmbio). Volta p/ Geral ao abrir.
  const [section, setSection] = useState<'geral' | 'cartao' | 'cambio' | 'tags'>('geral')
  useEffect(() => {
    if (open) {
      setInstallMode('avista')
      setInstallBasis('parcela')
      setSection('geral')
      // Descarta edições abandonadas do RHF ao reabrir o MESMO registro: como o
      // `values` não muda (mesma transação), o RHF não re-sincroniza sozinho, e os
      // campos ficariam com o que foi digitado antes de Cancelar.
      reset()
    }
  }, [open, transaction, duplicateFrom, reset])
  const installN = Math.max(2, parseInt(installCount || '2', 10))
  const isParcelado = canInstall && installMode === 'parcelado'
  // Valor de CADA parcela: no modo 'total' divide igualmente (resto de centavos
  // fica visível na prévia); 'parcela' = o valor digitado já é o de cada uma.
  const perParcela = isParcelado && installBasis === 'total'
    ? Math.round(((Number(amountValue) || 0) / installN) * 100) / 100
    : Number(amountValue) || 0
  const totalParcelado = perParcela * installN

  // Precisa do valor na moeda da conta quando a moeda da transação difere.
  const needsAccountAmount = !!selectedAccount && currency !== selectedAccount.currency
  const suggestedAccountAmount = needsAccountAmount
    ? (convertBetween(Number(amountValue) || 0, currency, selectedAccount!.currency, rates) ?? null)
    : null
  const effectiveAccountAmount = amountAccount ?? suggestedAccountAmount

  const tree = buildPicker(categories ?? [], kind)
  const accountOptions: ComboOption[] = accountComboOptions(accounts)
  const categoryOptions: ComboOption[] = [
    { value: 'none', label: t('dlg.noCategory') },
    ...tree.flatMap(({ parent, children }) => [
      { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
      ...children.map(
        (c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name }),
      ),
    ]),
  ]
  // GTQ (sem histórico) em data passada pode não ter taxa em cache => sugerir manual.
  const isBackdated = date < todayISO()
  const needsManualRate = currency !== baseCurrency && !hasHistorical(currency) && isBackdated

  async function onSubmit(values: FormValues) {
    const rateStr = values.exchange_rate?.trim()
    const manualRate = rateStr ? Number(rateStr) : null
    const payload: TransactionInput = {
      account_id: values.account_id,
      category_id: values.category_id || null,
      kind: values.kind,
      // parcelado: o valor gravado é o de CADA parcela (já dividido se modo 'total')
      amount: isParcelado ? perParcela : values.amount,
      currency, // sempre a moeda da conta
      date: values.date,
      description: values.description?.trim() || null,
      status: values.status,
      exchange_rate: manualRate && manualRate > 0 ? manualRate : null,
      // valor na moeda da conta (só quando difere); usa a sugestão se não editado.
      // No parcelado, é o valor da conta POR PARCELA.
      amount_account: needsAccountAmount
        ? isParcelado && installBasis === 'total' && effectiveAccountAmount != null
          ? Math.round((effectiveAccountAmount / installN) * 100) / 100
          : (effectiveAccountAmount ?? null)
        : null,
      tags,
      // só cartão tem fatura; fora dele o campo fica null
      statement_month: isCard && stmtMonth ? `${stmtMonth}-01` : null,
    }
    try {
      if (isParcelado) {
        // Valor EXATO de cada parcela (o resto de centavos vai nas primeiras). No
        // modo 'parcela' cada uma é o valor digitado; no 'total', divide o total.
        const amounts =
          installBasis === 'total'
            ? splitCents(Number(amountValue) || 0, installN)
            : Array.from({ length: installN }, () => perParcela)
        const amountsAccount = needsAccountAmount
          ? installBasis === 'total'
            ? splitCents(effectiveAccountAmount ?? 0, installN)
            : Array.from({ length: installN }, () => effectiveAccountAmount ?? null)
          : undefined
        await saveInstallments.mutateAsync({
          base: payload,
          count: installN,
          firstStatementMonth: stmtMonth || null,
          amounts,
          amountsAccount,
        })
        toast.success(t('dlg.tx.installSaved', { n: installN }))
      } else if (transaction && isRecurringEdit) {
        await editRecurring.mutateAsync({ transaction, values: payload, scope: editScope })
        toast.success(editScope === 'future' ? t('dlg.tx.savedUpdatedFuture') : t('dlg.tx.savedUpdated'))
      } else {
        await save.mutateAsync({ id: transaction?.id, values: payload })
        toast.success(transaction ? t('dlg.tx.savedUpdated') : t('dlg.tx.savedCreated'))
      }
      reset()
      setEditScope('one')
      onOpenChange(false)
    } catch (e) {
      toast.error(t('dlg.tx.saveError'), { description: (e as Error).message })
    }
  }

  const saving = save.isPending || editRecurring.isPending || saveInstallments.isPending

  // Câmbio só aparece quando a moeda difere (aí precisa do valor na conta e/ou taxa).
  const showFx = needsAccountAmount || currency !== baseCurrency
  const hasTags = (allTags ?? []).length > 0
  // Tags está SEMPRE disponível (para marcar na transação) ⇒ sempre há abas.
  const extraTabs = true
  // Se a aba ativa sumiu (trocou p/ conta não-cartão, mesma moeda…), cai em Geral.
  const activeSection =
    (section === 'cartao' && !isCard) || (section === 'cambio' && !showFx) ? 'geral' : section

  const geralFields = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="tx-description">{t('dlg.description')}</Label>
        <Input id="tx-description" {...register('description')} placeholder={t('dlg.tx.optional')} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="tx-amount">{t('dlg.amount')}</Label>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <MoneyInput id="tx-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
            )}
          />
          {errors.amount && <p className="text-destructive text-xs">{errors.amount.message}</p>}
        </div>
        {/* min-w-0 + w-full: o SelectTrigger é w-fit com whitespace-nowrap, então
            moedas de nome longo (ex.: "GTQ — Quetzal (Guatemala)") não encolhiam e
            transbordavam a coluna, criando barra de rolagem horizontal no miolo. */}
        <div className="grid min-w-0 gap-2">
          <Label htmlFor="tx-currency">{t('dlg.currency')}</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="tx-currency" className="w-full min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {enabledCurrencyList(enabledCurrencies).map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-2">
        <Label>{t('dlg.account')}</Label>
        <Combobox
          options={accountOptions}
          value={accountId}
          onChange={(v) => setValue('account_id', v, { shouldValidate: true })}
          placeholder={t('dlg.tx.selectAccount')}
          searchPlaceholder={t('dlg.searchAccount')}
          aria-label={t('dlg.account')}
        />
        {errors.account_id && <p className="text-destructive text-xs">{errors.account_id.message}</p>}
      </div>

      <div className="grid gap-2">
        <Label>{t('dlg.category')}</Label>
        <Combobox
          options={categoryOptions}
          value={watch('category_id') ?? 'none'}
          onChange={(v) => setValue('category_id', v === 'none' ? null : v)}
          placeholder={t('dlg.noCategory')}
          searchPlaceholder={t('dlg.searchCategory')}
          aria-label={t('dlg.category')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="tx-date">{t('dlg.date')}</Label>
          <DateInput id="tx-date" value={date} onChange={(iso) => setValue('date', iso, { shouldValidate: true })} />
          {errors.date && <p className="text-destructive text-xs">{errors.date.message}</p>}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="tx-status">{t('dlg.status')}</Label>
          <Select value={watch('status')} onValueChange={(v) => setValue('status', v as 'paid' | 'pending')}>
            <SelectTrigger id="tx-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid">{t('dlg.paid')}</SelectItem>
              <SelectItem value="pending">{t('dlg.pending')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

    </>
  )

  const tagsFields = hasTags ? (
    <div className="flex flex-wrap gap-1.5">
      {(allTags ?? []).map((t) => {
        const on = tags.includes(t.name)
        return (
          <button key={t.id} type="button" onClick={() => toggleTag(t.name)}>
            <Badge
              variant={on ? 'default' : 'outline'}
              className={cn('cursor-pointer', !on && 'text-muted-foreground hover:text-foreground')}
            >
              {t.name}
            </Badge>
          </button>
        )
      })}
    </div>
  ) : (
    <p className="text-muted-foreground text-sm">
      {t('dlg.tx.noTags')}
    </p>
  )

  const cardFields = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="tx-statement">
          {t('dlg.tx.statement')} <span className="text-muted-foreground font-normal">{t('dlg.tx.statementHint')}</span>
        </Label>
        <Input
          id="tx-statement"
          type="month"
          value={stmtMonth}
          min={date?.slice(0, 7)}
          onChange={(e) => {
            setStmtTouched(true)
            setStmtMonth(e.target.value)
          }}
          className="w-48"
        />
      </div>

      {canInstall && (
        <div className="grid gap-3 rounded-lg border p-3">
          <Tabs value={installMode} onValueChange={(v) => setInstallMode(v as 'avista' | 'parcelado')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="avista">{t('dlg.tx.avista')}</TabsTrigger>
              <TabsTrigger value="parcelado">{t('dlg.tx.parcelado')}</TabsTrigger>
            </TabsList>
          </Tabs>
          {isParcelado && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="tx-install-count">{t('dlg.tx.installCount')}</Label>
                  <Input id="tx-install-count" type="number" min={2} value={installCount} onChange={(e) => setInstallCount(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>{t('dlg.tx.installBasisQ')}</Label>
                  <Tabs value={installBasis} onValueChange={(v) => setInstallBasis(v as 'parcela' | 'total')}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="parcela">{t('dlg.tx.basisParcela')}</TabsTrigger>
                      <TabsTrigger value="total">{t('dlg.tx.basisTotal')}</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                {installN} × {formatCurrency(perParcela, currency)} ={' '}
                <span className="font-medium">{formatCurrency(totalParcelado, currency)}</span>
                {installBasis === 'total' && Math.abs(totalParcelado - (Number(amountValue) || 0)) > 0.005 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {' '}
                    {t('dlg.tx.installTotalAdjust', { value: formatCurrency(Number(amountValue) || 0, currency) })}
                  </span>
                )}
              </p>
            </>
          )}
        </div>
      )}
    </>
  )

  const fxFields = (
    <>
      {needsAccountAmount && (
        <div className="grid gap-2">
          <Label htmlFor="tx-amount-account">{t('dlg.tx.amountCharged', { currency: selectedAccount!.currency })}</Label>
          <MoneyInput id="tx-amount-account" value={effectiveAccountAmount ?? 0} onChange={(n) => setAmountAccount(n)} placeholder="0,00" />
        </div>
      )}
      {currency !== baseCurrency && (
        <div className="grid gap-2">
          <Label htmlFor="tx-rate">
            {t('dlg.tx.manualRate')}{' '}
            <span className="text-muted-foreground font-normal">
              {t('dlg.tx.manualRateHint', { currency, base: baseCurrency })}
            </span>
          </Label>
          <Input id="tx-rate" type="number" step="0.00000001" placeholder={t('dlg.tx.rateAuto')} {...register('exchange_rate')} />
          {needsManualRate && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t('dlg.tx.noHistRate', { currency })}
            </p>
          )}
        </div>
      )}
    </>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A rolagem fica no miolo, não no DialogContent: assim o menu do Combobox,
          que é portalado aqui dentro, pode transbordar o dialog sem ser cortado. */}
      <DialogContent className="flex max-h-[90svh] flex-col">
        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>
              {transaction ? t('dlg.tx.titleEdit') : isDuplicate ? t('dlg.tx.titleDuplicate') : t('dlg.tx.titleNew')}
            </DialogTitle>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto py-4">
            {isRecurringEdit && (
              <div className="grid gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <Label className="flex items-center gap-1 text-primary">
                  <Repeat className="size-4" /> {t('dlg.tx.recurringApply')}
                </Label>
                <Tabs value={editScope} onValueChange={(v) => setEditScope(v as EditScope)}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="one">{t('dlg.tx.scopeOne')}</TabsTrigger>
                    <TabsTrigger value="future">{t('dlg.tx.scopeFuture')}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}

            {/* Despesa / Receita — compacto */}
            <Tabs value={kind} onValueChange={(v) => setValue('kind', v as TransactionKind & ('income' | 'expense'))}>
              <TabsList className="grid w-full max-w-[240px] grid-cols-2">
                <TabsTrigger value="expense">{t('dlg.tx.expense')}</TabsTrigger>
                <TabsTrigger value="income">{t('dlg.tx.income')}</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Abas de seção só aparecem quando há Cartão e/ou Câmbio. Todo o
                conteúdo fica montado (hidden), então nada de valor se perde ao
                trocar de aba. */}
            {extraTabs ? (
              <>
                <Tabs
                  value={activeSection}
                  onValueChange={(v) => setSection(v as 'geral' | 'cartao' | 'cambio' | 'tags')}
                >
                  <TabsList>
                    <TabsTrigger value="geral">{t('dlg.tx.tabGeneral')}</TabsTrigger>
                    {isCard && <TabsTrigger value="cartao">{t('dlg.tx.tabCard')}</TabsTrigger>}
                    {showFx && <TabsTrigger value="cambio">{t('dlg.tx.tabFx')}</TabsTrigger>}
                    <TabsTrigger value="tags">{t('dlg.tx.tabTags')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className={activeSection === 'geral' ? 'grid gap-4' : 'hidden'}>{geralFields}</div>
                {isCard && (
                  <div className={activeSection === 'cartao' ? 'grid gap-4' : 'hidden'}>{cardFields}</div>
                )}
                {showFx && (
                  <div className={activeSection === 'cambio' ? 'grid gap-4' : 'hidden'}>{fxFields}</div>
                )}
                <div className={activeSection === 'tags' ? 'grid gap-4' : 'hidden'}>{tagsFields}</div>
              </>
            ) : (
              geralFields
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
